import { useEffect, useState } from "react";
import { useClinicContext } from "fe-web-components/clinic/ClinicContext";
import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";
import { ClinicUserContextApi, UserClinic } from "../clinicUserContext";
import { UserRoles } from "fe-common/graphql";
import { getCurrentUserEmail } from "user-management/src/cognito/kahunUserAuth";
import { MASTER_CLINIC_ID } from "fe-common/commonTypes/masterClinicDefaults";
import { clearLastSelectedClinicId } from "../clinicManage";

export function useClinicUser(clinicId: string): ClinicUserContextApi {
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState(null);
  const [userClinics, setUserClinics] = useState<UserClinic[]>([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isClinicUser, setIsClinicUser] = useState(false);
  const [username, setUsername] = useState("");
  const [userRoles, setUserRoles] = useState<UserRoles[]>(null);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [hasSuperAccess, setHasSuperAccess] = useState(false);
  const [hasManagerAccess, setHasManagerAccess] = useState(false);

  const clinicDao = useClinicContext();
  const webDao = useDaoContext();

  useEffect(() => {
    (async () => {
      setErrMsg(null);
      setLoading(true);
      try {
        const username = await getCurrentUserEmail();

        if (username) {
          setIsLoggedIn(true);
          setUsername(username);
          const roles = await webDao.getUserRoles();
          setUserRoles(roles as UserRoles[]);
          const isSuperAdmin = roles.includes(UserRoles.KahunClinicSuperAdmins);
          const isAdmin = roles.includes(UserRoles.KahunClinicAdmins);
          setHasSuperAccess(isSuperAdmin);
          setHasAdminAccess(isSuperAdmin || isAdmin);
          if (clinicId) {
            if (isSuperAdmin) {
              setHasManagerAccess(true);
            } else {
              const roles = await clinicDao.getCurrentClinicUser(clinicId);
              if (roles?.role) {
                setUserRoles((prev) => [...prev, roles.role]);
                setHasManagerAccess(
                  roles.role === UserRoles.KahunClinicManager,
                );
              }
            }
          }
          let clinics = await clinicDao.userClinics(true); //only favorite clinics
          const favClinicsIds = clinics ? clinics.map((c) => c.id) : [];
          if (isSuperAdmin || isAdmin)
            clinics = await clinicDao.userClinics(false); //all clinics

          setUserClinics(
            clinics.map((c) => ({
              ...c,
              isFavorite: favClinicsIds.includes(c.id),
            })),
          );

          let validClinicUser: boolean;
          if (clinicId === MASTER_CLINIC_ID) {
            //Only super admins can view the master clinic
            validClinicUser = isSuperAdmin;
          } else {
            validClinicUser =
              clinics.length && !!clinics.find((c) => c?.id === clinicId);
          }

          if (!validClinicUser) clearLastSelectedClinicId(username);
          setIsClinicUser(validClinicUser);
        } else {
          // If the user logged out make sure to clear the clinics
          clearUser();
        }
      } catch (e) {
        console.error(e);
        setErrMsg(e["message"]);
        clearUser();
      } finally {
        setLoading(false);
      }
    })();
  }, [clinicDao, webDao, clinicId]);

  const clearUser = () => {
    setErrMsg(null);
    setUserClinics(null);
    setIsLoggedIn(false);
    setUserRoles(null);
    setHasAdminAccess(false);
    setHasManagerAccess(false);
    setHasSuperAccess(false);
  };

  return {
    loading,
    errMsg,
    userClinics,
    isLoggedIn,
    isClinicUser,
    username,
    userRoles,
    hasAdminAccess,
    hasManagerAccess,
    hasSuperAccess,
    setUserClinics,
    clearUser,
  };
}
